import { Button } from '@/components/button';
import { useTranslation } from '@/contexts/i18n-context';
import styles from './policies-consent-page.module.css';
import { Section } from '@/components/containers/section';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { PageContainer } from '@/components/containers/page-container';
import clsx from 'clsx';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { useBranding } from '@/outlets/branding-outlet';

const translationBase = 'visit.policiesConsent';
const prescriptionCoverageBase = `${translationBase}.insuranceAttestation.prescriptionCoverage`;

export function PoliciesConsentPage() {
  const { t } = useTranslation();
  const {
    displayInsuranceAttestation,
    privacyPolicyUrl,
    termsOfUseUrl,
    privacyPolicySummary,
    termsOfUseSummary,
  } = useBranding();

  return (
    <PageContainer>
      <GradientFormTitle>
        <VisitNavigation />
        <h1 className="h3">{t(`${translationBase}.title`)}</h1>
        <p>{t(`${translationBase}.description`)}</p>
      </GradientFormTitle>
      {displayInsuranceAttestation && (
        <Section>
          <h2 className={clsx('h4', styles.sectionTitle)}>
            {t(`${translationBase}.insuranceAttestation.title`)}
          </h2>

          <h3 className={clsx('body-2', 'bold', styles.visitCoverageSubtitle)}>
            {t(`${translationBase}.insuranceAttestation.visitCoverage.title`)}
          </h3>
          <p>{t(`${translationBase}.insuranceAttestation.visitCoverage.body1`)}</p>
          <br />
          <p>
            {t(`${translationBase}.insuranceAttestation.visitCoverage.body2`)}
            <span className={styles.reimbursement}>
              {t(`${translationBase}.insuranceAttestation.visitCoverage.reimbursement`)}
            </span>
          </p>
          <br />
          <p>{t(`${translationBase}.insuranceAttestation.visitCoverage.body3`)}</p>

          <h3 className={clsx('body-2', 'bold', styles.prescriptionCoverageSubtitle)}>
            {t(`${translationBase}.insuranceAttestation.prescriptionCoverage.title`)}
          </h3>
          <p>
            <span className={clsx('bold', 'italic')}>
              {t(`${prescriptionCoverageBase}.commercialInsurance.bold`)}
            </span>
            {t(`${prescriptionCoverageBase}.commercialInsurance.body`)}
          </p>
          <br />
          <p>
            <span className={clsx('bold', 'italic')}>
              {t(`${prescriptionCoverageBase}.medicalBeneficiaries.bold`)}
            </span>
            {t(`${prescriptionCoverageBase}.medicalBeneficiaries.body`)}
          </p>
        </Section>
      )}
      {privacyPolicySummary && (
        <Section>
          <h2 className={clsx('h4', styles.sectionTitle)}>
            {t(`${translationBase}.privacyPolices.title`)}
          </h2>
          <div dangerouslySetInnerHTML={{ __html: privacyPolicySummary }} />
          <Button
            variant="link"
            as="a"
            to={privacyPolicyUrl}
            target="_blank"
            rightIcon="arrow_forward"
            className={styles.viewFullButton}
          >
            {t(`${translationBase}.privacyPolices.viewFull`)}
          </Button>
        </Section>
      )}
      {termsOfUseSummary && (
        <Section>
          <h2 className={clsx('h4', styles.sectionTitle)}>
            {t(`${translationBase}.termsAndConditions.title`)}
          </h2>
          <div dangerouslySetInnerHTML={{ __html: termsOfUseSummary }} />
          <Button
            variant="link"
            as="a"
            to={termsOfUseUrl}
            target="_blank"
            rightIcon="arrow_forward"
            className={styles.viewFullButton}
          >
            {t(`${translationBase}.termsAndConditions.viewFull`)}
          </Button>
        </Section>
      )}
      <Section className={styles.agreementSection}>
        <h2 className={clsx('body-2', 'bold', styles.visitCoverageSubtitle)}>
          {t(`${translationBase}.agreement.title`)}
        </h2>
        <ol className={styles.agreementList}>
          <li>{t(`${translationBase}.agreement.telemedicine`)}</li>
          <li>
            {t(`${translationBase}.agreement.privacyPolices.text`, {
              link: (
                <Button
                  variant="link"
                  as="a"
                  to={privacyPolicyUrl}
                  target="_blank"
                  key="notice-of-privacy-link"
                >
                  {t(`${translationBase}.agreement.privacyPolices.link`)}
                </Button>
              ),
            })}
          </li>
          <li>
            {t(`${translationBase}.agreement.termsOfUse.text`, {
              link: (
                <Button
                  variant="link"
                  as="a"
                  to={termsOfUseUrl}
                  target="_blank"
                  key="terms-of-use-link"
                >
                  {t(`${translationBase}.agreement.termsOfUse.link`)}
                </Button>
              ),
            })}
          </li>
        </ol>
      </Section>
      <VisitFormButtonRow
        nextStep={t(`${translationBase}.nextStep`)}
        buttonTextKey={`${translationBase}.agreeBtn`}
        as="a"
        to="/visit/pre-screen-questions"
      />
    </PageContainer>
  );
}
